<script lang="ts">
import { BreadcrumbItem } from "@/types/breadcrumbs"
import { Route } from "vue-router"
import { mapActions, mapGetters } from "vuex"

export default {
    name: "SidebarMenuItem",
    props: {
        item: { type: Object, required: true },
        collapse: { type: Boolean, required: true },
    },
    data() {
        return {
            isCollapsed: this.collapse,
            openItemId: null as string | number | null,
        }
    },
    computed: {
        ...mapGetters("sidebar", {
            breadcrumbPaths: "breadcrumbPaths",
        }),
        ...mapGetters("breadcrumbs", {
            currentBreadcrumbs: "items",
        }),
        route(): Route {
            // @ts-ignore
            return this.$route
        },
        router(): any {
            // @ts-ignore
            return this.$router
        },
        children() {
            return this.item.children || []
        },
        isDevice() {
            return this.item.type === "device"
        },
        iconClass() {
            switch (this.item.type) {
                case "company":
                    return "fa-landmark"
                case "location-group":
                    return "fa-sitemap"
                case "location":
                    return "fa-house"
                case "device-group":
                    return "fa-object-ungroup"
                default:
                    return null
            }
        },
        deviceCategoryIcon() {
            if (!this.isDevice) {
                return null
            }

            const category = this.item.data?.category?.value
                ? this.item.data.category.value
                : this.item.category?.value
                ? this.item.category.value
                : null

            switch (category) {
                case "OTHER":
                    return "icon-washing-machine"
                case "EV_CHARGER":
                    return "icon-car"
                case "SOLAR_POWER_PLANT":
                    return "icon-sun"
                case "BATTERY":
                    return "icon-battery"
                case "HYBRID_INVERTER":
                    return "icon-hybrid"
                case "HVAC":
                    return "icon-heat-pump"
                case "GRID":
                    return "icon-system"
                default:
                    return "icon-other"
            }
        },
        hasChildren() {
            return this.children.length > 0
        },
        itemLeftPadding() {
            const paddingStep = 10

            return `${paddingStep * this.item.level}px`
        },
        isActive() {
            return !this.isCollapsed && this.item.level <= 1
        },
        isSubItemActive() {
            return this.item.level > 1 && !this.isCollapsed
        },
    },
    methods: {
        ...mapActions("sidebar", {
            setActiveMenuItem: "setActiveMenuItem",
        }),
        toggleItem(e: MouseEvent) {
            e.preventDefault()
            const wasCollapsed = this.isCollapsed
            this.isCollapsed = !this.isCollapsed
            if (wasCollapsed) {
                this.$emit("item-opened", this.item.id)
            }
        },
        onItemClick(e: MouseEvent) {
            this.setActiveMenuItem(this.item)

            const route = this.item.href

            if (this.item.children?.length) {
                this.toggleItem(e)
            }

            if (this.route.path === route) {
                return
            }

            if (route.length === 0) {
                return
            }

            this.router.push(route)
        },
        checkCollapsed(item: any) {
            const isCurrentRoute =
                this.$route.path === item.href || this.$route.path.startsWith(item.href + "/")

            const isInBreadcrumbTrail = this.currentBreadcrumbs.some(
                (crumb: BreadcrumbItem) => crumb.path === item.href,
            )

            if (isCurrentRoute || isInBreadcrumbTrail || item.filtered) {
                return false
            }

            if (item.children?.length) {
                const isChildActive = item.children.some(
                    (child: any) =>
                        this.$route.path === child.href ||
                        this.$route.path.startsWith(child.href + "/"),
                )
                if (isChildActive) {
                    return false
                }
            }

            return true
        },
        handleInitialState() {
            if (
                this.$route.path === this.item.href ||
                this.$route.path.startsWith(this.item.href + "/")
            ) {
                this.isCollapsed = false
                this.setActiveMenuItem(this.item)
            }

            this.handleRouteChange()
        },
        handleRouteChange() {
            this.isCollapsed = this.checkCollapsed(this.item)

            if (this.$route.path === this.item.href) {
                this.setActiveMenuItem(this.item)
            }
        },
    },
    watch: {
        collapse(val: boolean) {
            if (val) {
                this.isCollapsed = true
            }
        },
        $route() {
            this.handleRouteChange()
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.handleInitialState()
        })
    },
}
</script>

<template>
    <div
        class="sidebar-menu-item"
        :class="[
            `menu-item-level-${item.level}`,
            {
                'menu-item-active': isActive,
                'sub-menu-item-active': isSubItemActive,
                'filtered-title': item?.filteredTitle,
            },
        ]"
        @click.stop="(e) => onItemClick(e)"
    >
        <a
            :href="item.href"
            class="menu-item-title"
            :style="{ paddingLeft: itemLeftPadding }"
            @click.prevent="(e) => e.preventDefault()"
        >
            <span
                v-if="item?.filteredTitle"
                class="fa fa-solid fa-circle fa-2xs filtered-indicator"
            ></span>
            <div class="d-flex justify-content-between align-items-center">
                <span v-if="isDevice" :class="['fa', `icon`, deviceCategoryIcon, `mr-2`]"></span>
                <span v-else :class="['fa', 'fa-solid', 'fa-lg', iconClass, 'mr-2']"></span>
                <span>{{ item.title }} </span>
            </div>
            <template v-if="hasChildren">
                <span @click.stop="(e) => toggleItem(e)" class="expand-icon-wrapper">
                    <i v-if="isCollapsed" class="fa fa-plus"></i>
                    <i v-else class="fa fa-minus"></i>
                </span>
            </template>
        </a>

        <div
            v-if="hasChildren"
            class="menu-item-children"
            :class="{ collapsed: isCollapsed }"
            :id="`children-${item.id}`"
        >
            <SidebarMenuItem
                :id="childItem.href"
                :item="childItem"
                :key="`${childItem.id}-${childItem.href}`"
                v-for="childItem in children"
                :collapse="
                    checkCollapsed(childItem) ||
                    (openItemId !== null && openItemId !== childItem.id)
                "
                @item-opened="openItemId = $event"
            />
        </div>
    </div>
</template>

<style>
.menu-item-title {
    &:hover {
        text-decoration: none;
    }
}

.expand-icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    margin: -5px;
    cursor: pointer;
}
</style>

<style scoped>
.collapsed {
    display: none;
}
</style>
